/**
 * Mobile optimization utilities
 * Provides hooks and helpers for touch events, responsive design, and viewport optimizations
 */
import { TouchEvent, useEffect, useRef, useState } from 'react';

/**
 * Hook to detect touch capability
 * @returns Boolean indicating if the device supports touch
 */
export const useIsTouchDevice = (): boolean => {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    const isTouchDevice =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-expect-error - For IE11 support
      navigator.msMaxTouchPoints > 0;

    setIsTouch(isTouchDevice);
  }, []);

  return isTouch;
};

/**
 * Hook to handle swipe gestures
 * @param onSwipeLeft Callback for left swipe
 * @param onSwipeRight Callback for right swipe
 * @param onSwipeUp Callback for up swipe
 * @param onSwipeDown Callback for down swipe
 * @param threshold Minimum distance for swipe detection (px)
 */
export const useSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
}: {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number;
}) => {
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const touchEnd = useRef<{ x: number; y: number } | null>(null);

  // Handle touch start
  const handleTouchStart = (e: TouchEvent) => {
    touchEnd.current = null;
    touchStart.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    };
  };

  // Handle touch move
  const handleTouchMove = (e: TouchEvent) => {
    touchEnd.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    };
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;

    const distX = touchStart.current.x - touchEnd.current.x;
    const distY = touchStart.current.y - touchEnd.current.y;

    const isHorizontalSwipe = Math.abs(distX) > Math.abs(distY);

    if (isHorizontalSwipe) {
      if (Math.abs(distX) > threshold) {
        if (distX > 0 && onSwipeLeft) {
          onSwipeLeft();
        } else if (distX < 0 && onSwipeRight) {
          onSwipeRight();
        }
      }
    } else {
      if (Math.abs(distY) > threshold) {
        if (distY > 0 && onSwipeUp) {
          onSwipeUp();
        } else if (distY < 0 && onSwipeDown) {
          onSwipeDown();
        }
      }
    }

    // Reset values
    touchStart.current = null;
    touchEnd.current = null;
  };

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};

/**
 * Hook to handle viewport changes and orientation
 */
export const useViewport = () => {
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
    isMobile: window.innerWidth < 768,
  });

  useEffect(() => {
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
        orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
        isMobile: window.innerWidth < 768,
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return viewport;
};

/**
 * Helper to generate responsive sizes based on viewport
 * @param base Base size in pixels
 * @param mobileScale Scale factor for mobile devices
 * @param tabletScale Scale factor for tablet devices
 * @returns Responsive size in pixels
 */
export const getResponsiveSize = (base: number, mobileScale = 0.7, tabletScale = 0.85): number => {
  if (typeof window === 'undefined') return base;

  const width = window.innerWidth;

  if (width < 480) return Math.round(base * mobileScale);
  if (width < 768) return Math.round(base * tabletScale);

  return base;
};

/**
 * Apply touch-friendly styles to an element
 * @returns CSS properties object for touch-friendly elements
 */
export const getTouchFriendlyStyles = (isTouch = false): React.CSSProperties => {
  if (!isTouch) return {};

  return {
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    userSelect: 'none',
    // Increase tap target size
    padding: '0.5rem',
    margin: '-0.5rem',
  };
};

/**
 * Helper to optimize viewport meta tag for mobile devices
 */
export const optimizeViewport = (): void => {
  // Find the viewport meta tag
  let viewportMeta = document.querySelector('meta[name="viewport"]');

  // If it doesn't exist, create it
  if (!viewportMeta) {
    viewportMeta = document.createElement('meta');
    viewportMeta.setAttribute('name', 'viewport');
    document.head.appendChild(viewportMeta);
  }

  // Set optimal viewport settings
  viewportMeta.setAttribute(
    'content',
    'width=device-width, initial-scale=1.0, viewport-fit=cover, maximum-scale=5.0'
  );
};

/**
 * Initialize mobile optimizations
 * Call this function once at app startup
 */
export const initMobileOptimizations = (): void => {
  // Optimize viewport
  optimizeViewport();

  // Add touch-action to html element
  document.documentElement.style.touchAction = 'manipulation';

  // Disable double-tap zoom on touch devices
  const style = document.createElement('style');
  style.textContent = `
    * {
      touch-action: manipulation;
    }
    
    input, textarea, select, button {
      font-size: 16px; /* Prevent iOS zoom on focus */
    }
    
    @media (max-width: 768px) {
      .touch-target {
        min-height: 44px;
        min-width: 44px;
      }
    }
  `;
  document.head.appendChild(style);
};
